import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Dispatch } from 'src/config/rootStore';
import { getFeatureFlags } from 'src/modules/user/selectors/featureFlags';
import {
    getUserInformation,
    shouldTakeToRequestPersonalEmailAddressFlow,
    shouldTakeToRequestPersonalEmailAddressFlowRequiredOnly,
} from 'src/modules/user/selectors/userSelectors';
import image from 'src/public/img/marginalia-uploading.svg';
import { Button } from 'src/shared/components/Button';
import { ContactUsModal } from 'src/shared/components/ContactUsModal';
import { Input } from 'src/shared/components/Input';
import { isEmailValid } from 'src/shared/helpers/emailValidator';
import { color, font } from 'src/shared/styles/variables';
import arrowIcon from 'src/shared/svg/icon-arrow-back.svg';

import { BrandWrapper } from '../components/BrandWrapper';
import { isOnboardingCompleted } from '../selectors/onboardingSelectors';

const Wrapper = styled.div({
    maxWidth: 950,
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    paddingBottom: 200,
});

const InnerWrapper = styled.div({
    maxWidth: 505,
});

const HeaderText = styled.h3({
    color: color.pickledBluewood,
    marginBottom: 0,
    ...font.black(font.size.extraLarge),
});

const DescriptionText = styled.p({
    color: color.blueBayoux,
    marginBottom: 23,
    lineHeight: '26px',
    ...font.book(font.size.medium),
});

const BoldDescriptionText = styled(DescriptionText)({
    marginBottom: 0,
    marginTop: 20,
    ...font.heavy(font.size.medium),
});

const BoldDescriptionTextInline = styled(BoldDescriptionText)({
    display: 'inline',
});

const VerificationEmailSentText = styled(DescriptionText)({
    marginBottom: 0,
});

const HavingTroubleText = styled(BoldDescriptionText)({
    color: color.royalBlue,
    marginTop: 0,
    marginBottom: 30,
    cursor: 'pointer',
});

const BackArrowIcon = styled.img({
    transform: 'rotate(180deg)',
    marginRight: 12,
    paddingTop: 3,
});

const InputWrapper = styled.div({
    maxWidth: 380,
});

const ButtonsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});

export enum ProvidePersonalEmailAddressPageTestID {
    ProvideEmailAddressStep = 'ProvidePersonalEmailAddressPageTestID.ProvideEmailAddressStep',
    VerifyEmailAddressStep = 'ProvidePersonalEmailAddressPageTestID.VerifyEmailAddressStep',
    ErrorMessage = 'ProvidePersonalEmailAddressPageTestID.ErrorMessage',
    ContactUsModal = 'ProvidePersonalEmailAddressPageTestID.ContactUsModal',
}

export const ProvidePersonalEmailAddressPage: React.FC<RouteComponentProps> = ({ history }) => {
    const [email, setEmail] = useState('');
    const [triedToSubmit, setTriedToSubmit] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorFromBackend, setErrorFromBackend] = useState('');
    const [verificationEmailResent, setVerificationEmailResent] = useState(false);
    const [contactUsModalVisible, setContactUsModalVisible] = useState(false);
    const [forceFirstStepToShow, setForceFirstStepToShow] = useState(false);

    const { t } = useTranslation();

    const user = useSelector(getUserInformation);
    const shouldRenderInOnboarding = useSelector(shouldTakeToRequestPersonalEmailAddressFlow);
    const shouldRenderOutsideOnboarding = useSelector(
        shouldTakeToRequestPersonalEmailAddressFlowRequiredOnly,
    );
    const { workEmailAddressChangeRequired } = useSelector(getFeatureFlags);
    const isActiveCobraRifMember = user.isActiveCobraRifMember;
    const onboardingCompleted = useSelector(isOnboardingCompleted);

    const dispatch = useDispatch<Dispatch>();

    const goToTheNextPage = () => {
        history.replace(onboardingCompleted ? '/' : '/welcome');
    };

    useEffect(() => {
        if (
            !workEmailAddressChangeRequired &&
            user.newEmailAddressNotConfirmed != null &&
            user.newEmailAddressNotConfirmed != ''
        ) {
            goToTheNextPage();
        }
    }, [user, workEmailAddressChangeRequired, goToTheNextPage]);

    useEffect(() => {
        if (
            (onboardingCompleted && !shouldRenderOutsideOnboarding) ||
            (!onboardingCompleted && !shouldRenderInOnboarding)
        ) {
            goToTheNextPage();
        }
    }, [
        shouldRenderInOnboarding,
        shouldRenderOutsideOnboarding,
        onboardingCompleted,
        goToTheNextPage,
    ]);

    const newEmailValid = isEmailValid(email);
    const newEmailTheSameAsOldOne = email === user.eligiblePersonEmail || email == user.email;

    const formValid = newEmailValid && !newEmailTheSameAsOldOne;

    const handleSubmit = async () => {
        setTriedToSubmit(true);

        if (!formValid) {
            return;
        }

        try {
            setSubmitting(true);

            await dispatch.user.updateEmailAddress(email);

            setForceFirstStepToShow(false);
        } catch (error) {
            if (typeof error === 'string') {
                setErrorFromBackend(error);
            }
        } finally {
            setTriedToSubmit(false);
            setSubmitting(false);
        }
    };

    const handleResendVerificationEmail = async () => {
        try {
            setVerificationEmailResent(true);
            await dispatch.user.updateEmailAddress(user.newEmailAddressNotConfirmed);
        } catch (error) {
            Sentry.captureException(error);
        }
    };

    let errorMessage = '';

    if (triedToSubmit && newEmailTheSameAsOldOne) {
        errorMessage = t('onboarding:provide_personal_email_address_page.errors.work_email');
    }

    if (triedToSubmit && !newEmailValid) {
        errorMessage = t('onboarding:provide_personal_email_address_page.errors.invalid_email');
    }

    const showVerifyEmailStep =
        workEmailAddressChangeRequired &&
        !forceFirstStepToShow &&
        user.newEmailAddressNotConfirmed != null &&
        user.newEmailAddressNotConfirmed != '';

    return (
        <BrandWrapper>
            <Wrapper>
                <InnerWrapper>
                    {showVerifyEmailStep ? (
                        <div
                            data-testid={
                                ProvidePersonalEmailAddressPageTestID.VerifyEmailAddressStep
                            }
                        >
                            <HeaderText css={{ marginBottom: 20 }}>
                                {t('onboarding:provide_personal_email_address_page.title')}
                            </HeaderText>

                            <DescriptionText>
                                <Trans
                                    t={t}
                                    ns="onboarding"
                                    i18nKey="provide_personal_email_address_page.description"
                                    values={{ email: user.newEmailAddressNotConfirmed }}
                                    components={{
                                        bold: <BoldDescriptionTextInline />,
                                    }}
                                />
                            </DescriptionText>

                            {verificationEmailResent ? (
                                <>
                                    <VerificationEmailSentText>
                                        {t(
                                            'onboarding:provide_personal_email_address_page.verification_email_sent',
                                        )}
                                    </VerificationEmailSentText>
                                    <HavingTroubleText
                                        onClick={() => setContactUsModalVisible(true)}
                                    >
                                        {t(
                                            'onboarding:provide_personal_email_address_page.still_having_trouble',
                                        )}
                                    </HavingTroubleText>
                                </>
                            ) : (
                                <>
                                    <VerificationEmailSentText>
                                        {t(
                                            'onboarding:provide_personal_email_address_page.didnt_get_the_email',
                                        )}
                                    </VerificationEmailSentText>
                                    <HavingTroubleText onClick={handleResendVerificationEmail}>
                                        {t(
                                            'onboarding:provide_personal_email_address_page.resend_verification_email',
                                        )}
                                    </HavingTroubleText>
                                </>
                            )}

                            <DescriptionText
                                css={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setForceFirstStepToShow(true);
                                    setEmail(user.newEmailAddressNotConfirmed as string);
                                }}
                            >
                                <BackArrowIcon src={arrowIcon} />
                                {t(
                                    'onboarding:provide_personal_email_address_page.change_preferred_email_address',
                                )}
                            </DescriptionText>

                            <ContactUsModal
                                dataTestID={ProvidePersonalEmailAddressPageTestID.ContactUsModal}
                                visible={contactUsModalVisible}
                                onToggle={() => setContactUsModalVisible(visible => !visible)}
                                header={t(
                                    'onboarding:provide_personal_email_address_page.contact_modal.title',
                                )}
                                description={t(
                                    'onboarding:provide_personal_email_address_page.contact_modal.description',
                                )}
                                tags={['provide_personal_email_address_page_contact_us_modal']}
                                helpDesk
                            />
                        </div>
                    ) : (
                        <div
                            data-testid={
                                ProvidePersonalEmailAddressPageTestID.ProvideEmailAddressStep
                            }
                        >
                            <HeaderText>
                                {t(
                                    'onboarding:provide_personal_email_address_page.provide_email_step.title',
                                    {
                                        firstName: user.firstName,
                                    },
                                )}
                            </HeaderText>
                            <HeaderText>
                                {t(
                                    'onboarding:provide_personal_email_address_page.provide_email_step.subtitle',
                                )}
                            </HeaderText>
                            <BoldDescriptionText>
                                {t(
                                    `onboarding:provide_personal_email_address_page.provide_email_step.${
                                        onboardingCompleted
                                            ? 'description_after_onboarding'
                                            : 'description_before_onboarding'
                                    }`,
                                )}
                            </BoldDescriptionText>
                            <DescriptionText>
                                {t(
                                    'onboarding:provide_personal_email_address_page.provide_email_step.description',
                                )}
                            </DescriptionText>
                            <InputWrapper>
                                <Input
                                    fieldId="email"
                                    label={t(
                                        'onboarding:provide_personal_email_address_page.provide_email_step.preferred_email',
                                    )}
                                    type="text"
                                    placeholder={t(
                                        'onboarding:provide_personal_email_address_page.provide_email_step.personal_email_address',
                                    )}
                                    value={email}
                                    error={errorFromBackend || errorMessage}
                                    onChange={newEmail => {
                                        setEmail(newEmail);
                                        setErrorFromBackend('');
                                    }}
                                    errorMessageTestID={
                                        ProvidePersonalEmailAddressPageTestID.ErrorMessage
                                    }
                                />

                                <ButtonsWrapper>
                                    <Button
                                        onClick={handleSubmit}
                                        widthPercentage={workEmailAddressChangeRequired ? 100 : 48}
                                        marginTop={32}
                                        loading={submitting}
                                        noHorizontalPadding={!workEmailAddressChangeRequired}
                                    >
                                        {t('common.submit')}
                                    </Button>
                                    {workEmailAddressChangeRequired ||
                                    isActiveCobraRifMember ? null : (
                                        <Button
                                            onClick={goToTheNextPage}
                                            marginTop={32}
                                            widthPercentage={48}
                                            type="noBackground"
                                            noHorizontalPadding
                                        >
                                            {t(
                                                'onboarding:provide_personal_email_address_page.provide_email_step.skip_this_step',
                                            )}
                                        </Button>
                                    )}
                                </ButtonsWrapper>
                            </InputWrapper>
                        </div>
                    )}
                </InnerWrapper>
                <img src={image} />
            </Wrapper>
        </BrandWrapper>
    );
};
